import { deriveCameraGraphNavigationPhase } from '@toolproof-core/visualization/_pure/camera-graph';
import type { CameraGraph } from '@toolproof-core/visualization/_contract/camera';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toPrimitiveCameraViewId, toPrimitiveStandoffStationId, withPrimitiveCameraDestination } from '@/adapters/primitive/_pure/derivation/cameraGraph';
import { toPrimitivePowerConstellationKey } from '@/adapters/primitive/_pure/derivation/keyCodec';
import { buildPrimitiveRodFrame, toScopedPrimitiveRodKey, type PrimitiveRodPlacement } from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { toPrimitiveCameraSteps } from '@/adapters/primitive/_pure/derivation/wheels';
import { selectPrimitiveDirectionEffects } from '@/adapters/primitive/_pure/derivation/primitiveDirectionRules';
import { selectPrimitiveCameraDirections } from '@/adapters/primitive/_pure/derivation/primitiveCameraRules';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveCameraDirectionEffect } from '@/adapters/primitive/_contract/_~generated/cameraDirectionEffect';
import type { PrimitiveOperationDirectionEffect } from '@/adapters/primitive/_contract/_~generated/operationDirectionEffect';
import type { NaturalContributionRef, NaturalEvent, NaturalOperandWheelRef } from '@/adapters/primitive/_contract/natural/event';
import type { PrimitiveCameraConfig } from '@/adapters/primitive/_contract/camera';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

function deriveDispatchCue(args: {
    base: SpaceDerivationFrame;
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    movingEntity: NaturalContributionRef | NaturalOperandWheelRef;
    contour: boolean;
    /** The rendered rod scope (workflow operation rods); cue keys must match the scoped items. */
    scope?: string;
}) {
    const frame = buildPrimitiveRodFrame({
        spaceKey: args.base.spaceKey, config: args.config, wheels: args.wheels,
        ...(args.scope === undefined ? {} : { scope: args.scope }),
    });
    const source = args.wheels.find((wheel) => wheel.role === args.movingEntity.wheelRole && wheel.variant === undefined);
    if (!source) throw new Error('Dispatch attention cue requires the emitting operand wheel.');
    const powers = !args.contour ? [] : 'power' in args.movingEntity ? [args.movingEntity.power] : source.powers;
    const targets = [];
    for (const power of powers) {
        // Refused deficit settlement can dispatch an empty band above the operand's declared powers.
        if (!source.powers.includes(power)) continue;
        const key = toScopedPrimitiveRodKey(toPrimitivePowerConstellationKey({ wheelRole: source.role, power }), args.scope);
        const constellation = frame.constellations.find((entry) => entry.constellationKey === key);
        // Placeholder operands (e.g. an empty quotient) render no digit cells, so there is nothing to outline.
        if (!constellation && source.valueKind === 'placeholder') continue;
        if (!constellation || constellation.constellationKind !== 'entity') throw new Error('Dispatch attention cue requires an emitting band.');
        targets.push({ targetKind: 'constellation' as const, constellationKey: key });
    }
    const previous = args.base.spaceRenderOptions?.contourEmphases ?? [];
    return {
        highlight: { spaceRenderOptions: { contourEmphases: [
            ...previous,
            ...(targets.length ? [{ targets, color: args.config.base.display.emphasis.color, lifetime: 'while-present' as const }] : []),
        ] } },
        restore: { spaceRenderOptions: { contourEmphases: previous } },
    };
}

type NaturalCameraAction = {
    keyframe: number;
    view: string;
    dispatch: boolean;
    /** Presentation-only copies have no arithmetic takeoff event. */
    dispatchSource?: NaturalOperandWheelRef;
    /** A result settlement precedes this dispatch, so the settled state is held before panning away. */
    afterSettlement: boolean;
    /** Station changes (standoff entry/return) complete before the keyframe instead of riding along with it. */
    sequential?: boolean;
    /** Hold the current state before navigating, e.g. a settled Assembly before returning. */
    holdBefore?: boolean;
    /** Dispatch cued where the camera already is; the flight's own pan carries the aim to the receiver. */
    inPlace?: boolean;
    /** The camera already holds this view; only the optional hold precedes the keyframe. */
    stay?: boolean;
};

/** Carry Assemblies (addition and multiplication) are watched from the paired Composite-settlement station. */
type StandoffState = 'idle' | 'pending' | 'active' | 'returning';

/** Workflow operation rods are placed on the ToolStep radials; anchors must use the rendered placement. */
type NaturalRodPlacing = {
    placement?: PrimitiveRodPlacement;
    postPlacementOverride?: (wheel: PrimitiveWheelState) => PrimitiveWheelState;
};

function toTakeoffMovingEntity(event: NaturalEvent): NaturalContributionRef | NaturalOperandWheelRef | undefined {
    if (event.eventKind === 'contribution-traveller-taken-off') return event.contributionTraveller;
    if (event.eventKind === 'operand-wheel-transfer-started') return event.operandWheel;
    return undefined;
}

function deriveFlightAnchor(args: {
    spaceKey: SpaceDerivationFrame['spaceKey'];
    config: Config;
    scope: string;
    wheels: readonly PrimitiveWheelState[];
    movingEntity: NaturalContributionRef | NaturalOperandWheelRef;
    /** Anchor on the stable (receiver) wheel instead of the flight. */
    stable?: boolean;
    placing?: NaturalRodPlacing;
}): Vector3Tuple | undefined {
    const variant = args.stable ? undefined : 'flight';
    const wheel = args.wheels.find((entry) => entry.role === args.movingEntity.wheelRole && entry.variant === variant);
    if (!wheel) return undefined;
    const frame = buildPrimitiveRodFrame({
        spaceKey: args.spaceKey, config: args.config, wheels: [wheel], scope: args.scope,
        ...(args.placing?.placement ? { placement: args.placing.placement } : {}),
        ...(args.placing?.postPlacementOverride ? { postPlacementOverride: args.placing.postPlacementOverride } : {}),
    });
    const power = 'power' in args.movingEntity ? args.movingEntity.power : wheel.powers[0];
    if (power === undefined) return undefined;
    const key = toPrimitivePowerConstellationKey({ wheelRole: args.movingEntity.wheelRole, ...(variant ? { variant } : {}), power });
    const anchor = frame.constellationParametricState?.entityConstellationByKey?.[toScopedPrimitiveRodKey(key, args.scope)]?.layout?.anchor;
    return anchor?.anchorKind === 'position' ? [...anchor.position] : undefined;
}

/** A Contribution-traveller that settles back on its own wheel is watched from where the camera is. */
function isSelfIncrement(steps: readonly PrimitiveStepState[], stepIndex: number, keyframeIndex: number, movingEntity: NaturalContributionRef | NaturalOperandWheelRef) {
    for (let index = stepIndex; index < steps.length; index++) {
        const keyframes = steps[index]!.keyframes;
        for (let at = index === stepIndex ? keyframeIndex + 1 : 0; at < keyframes.length; at++) {
            for (const { event } of keyframes[at]!.events) {
                if (event.eventKind !== 'contribution-traveller-aligned' && event.eventKind !== 'contribution-traveller-settled-on-result') continue;
                if (!('contributionTraveller' in event) || event.contributionTraveller.wheelRole !== movingEntity.wheelRole) continue;
                return 'receiverInteraction' in event && event.receiverInteraction?.receiver.wheelRole === movingEntity.wheelRole;
            }
        }
    }
    return false;
}

/** The next dispatch subject after the given keyframe, if any. */
function findNextMovingEntity(steps: readonly PrimitiveStepState[], stepIndex: number, keyframeIndex: number) {
    for (let index = stepIndex; index < steps.length; index++) {
        const keyframes = steps[index]!.keyframes;
        for (let at = index === stepIndex ? keyframeIndex + 1 : 0; at < keyframes.length; at++) {
            const emission = keyframes[at]!.events.find(({ event }) =>
                event.eventKind === 'contribution-traveller-taken-off' || event.eventKind === 'operand-wheel-transfer-started')?.event;
            if (emission) return toTakeoffMovingEntity(emission);
        }
    }
    return undefined;
}

/** The stable takeoff anchor of the next dispatch, unless another unit decrement comes first. */
function deriveNextTakeoffAnchor(args: {
    spaceKey: SpaceDerivationFrame['spaceKey'];
    config: Config;
    scope: string;
    steps: readonly PrimitiveStepState[];
    stepIndex: number;
    keyframeIndex: number;
    placing?: NaturalRodPlacing;
}): Vector3Tuple | undefined {
    let previous = args.steps[args.stepIndex]!.keyframes[args.keyframeIndex]!;
    for (let stepIndex = args.stepIndex; stepIndex < args.steps.length; stepIndex++) {
        const keyframes = args.steps[stepIndex]!.keyframes;
        for (let keyframeIndex = stepIndex === args.stepIndex ? args.keyframeIndex + 1 : 0; keyframeIndex < keyframes.length; keyframeIndex++) {
            const keyframe = keyframes[keyframeIndex]!;
            if (keyframe.events.some(({ event }) => event.eventKind === 'unit-introduced')) return undefined;
            const emission = keyframe.events.find(({ event }) =>
                event.eventKind === 'contribution-traveller-taken-off' || event.eventKind === 'operand-wheel-transfer-started')?.event;
            if (emission) {
                const movingEntity = toTakeoffMovingEntity(emission);
                if (!movingEntity) return undefined;
                return deriveFlightAnchor({
                    spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing, wheels: previous.wheels,
                    movingEntity,
                    stable: true,
                });
            }
            previous = keyframe;
        }
    }
    return undefined;
}

export function deriveNaturalCameraChoreography(input: {
    spaceKey: SpaceDerivationFrame['spaceKey'];
    config: Config;
    scope: string;
    steps: readonly PrimitiveStepState[];
    graph: CameraGraph;
    viewByStepIndex: readonly string[];
    placing?: NaturalRodPlacing;
    /** The view the camera holds before the first dispatch; defaults to the primitive-space `overview` alias. */
    overviewView?: string;
    cameraCatalog?: DirectionCatalog<PrimitiveCameraDirectionEffect>;
}) {
    const args = { ...input, steps: toPrimitiveCameraSteps(input.steps) };
    const graph: CameraGraph = { ...args.graph, views: { ...args.graph.views } };
    const actions: NaturalCameraAction[][] = [];
    const endViews: string[] = [];
    let movingEntity: NaturalContributionRef | NaturalOperandWheelRef | undefined;
    let currentView = args.overviewView ?? 'overview';
    let stationId: string | undefined;
    let receiverTarget: Vector3Tuple | undefined;
    let lastTarget: Vector3Tuple | undefined;
    let returnToReceiver = false;
    let settledSinceDispatch = false;
    let decrementedSinceDispatch = false;
    let decrementView: { station: string; aim: Vector3Tuple } | undefined;
    let firstDispatch = true;
    let openingMultiplicationAim: Vector3Tuple | undefined;
    let selfIncrement = false;
    let awaitingRevival: NaturalContributionRef | NaturalOperandWheelRef | undefined;
    let alignedContributionTraveller: NaturalContributionRef | NaturalOperandWheelRef | undefined;
    let standoff: StandoffState = 'idle';
    let standoffTarget: Vector3Tuple | undefined;
    // A unit decrement (multiplication) is aimed at its Multiplier band, or between it and the next takeoff band
    // when another dispatch follows, so the decrement and the reviving Multiplicand are framed together.
    const deriveDecrementAim = (stepIndex: number, keyframeIndex: number) => {
        const step = args.steps[stepIndex]!;
        const keyframe = step.keyframes[keyframeIndex]!;
        const unitIntroduction = keyframe.events.find(({ event }) => event.eventKind === 'unit-introduced')?.event;
        if (unitIntroduction?.eventKind !== 'unit-introduced') return undefined;
        const unitTarget = deriveFlightAnchor({
            spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing, wheels: keyframe.wheels,
            movingEntity: unitIntroduction.receiverInteraction.receiver, stable: true,
        });
        if (!unitTarget) throw new Error(`Natural unit decrement has no receiver anchor in ${step.stepKey}.`);
        const takeoff = deriveNextTakeoffAnchor({
            spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing, steps: args.steps, stepIndex, keyframeIndex,
        });
        const aim: Vector3Tuple = takeoff
            ? [0, 1, 2].map((axis) => (unitTarget[axis]! + takeoff[axis]!) / 2) as Vector3Tuple
            : unitTarget;
        return { aim, takeoff };
    };
    for (const [stepIndex, step] of args.steps.entries()) {
        if (!step.moment) throw new Error(`Natural camera direction requires a moment for '${step.stepKey}'.`);
        const directions = selectPrimitiveCameraDirections(step.moment, args.cameraCatalog);
        const stepActions: typeof actions[number] = [];
        if (step.moment.frames.some(frame => frame.kind === 'multiplication-comparison' || frame.kind === 'division-comparison')
            || step.moment.step.kind === 'multiplication-unit') {
            const view = args.viewByStepIndex[stepIndex]!;
            if (!graph.views[view]) throw new Error(`Embedded comparison has no camera view: ${step.stepKey}`);
            if (directions.has('embedded-view')) {
                stepActions.push({ keyframe: 0, view, dispatch: false, afterSettlement: false,
                    sequential: true, stay: view === currentView });
                currentView = view;
            }
            lastTarget = undefined;
            decrementView = undefined;
            actions.push(stepActions);
            endViews.push(currentView);
            continue;
        }
        const record = (
            cue: PrimitiveCameraDirectionEffect['cue'],
            action: Omit<NaturalCameraAction, 'sequential' | 'holdBefore'>,
            station: string, subject: Vector3Tuple,
        ) => {
            const direction = directions.get(cue);
            if (!direction) return;
            const target = direction.target === 'subject' ? subject : receiverTarget;
            if (!target) throw new Error(`Camera cue '${cue}' has no ${direction.target} anchor in ${step.stepKey}.`);
            if (direction.target === 'receiver') {
                if (!stationId) throw new Error(`Camera cue '${cue}' has no receiver station in ${step.stepKey}.`);
                station = stationId;
            }
            if ((cue === 'flight' || cue === 'settlement' || cue === 'receiver-return')
                && lastTarget && target.every((value, axis) => value === lastTarget![axis])) return;
            const stay = action.stay && lastTarget !== undefined && target.every((value, axis) => value === lastTarget![axis]);
            // A stay never navigates, so it keeps the view the camera already holds.
            if (!stay) {
                currentView = toPrimitiveCameraViewId(args.scope, `natural:${stepIndex}:${action.keyframe}`);
                graph.views[currentView] = { station, target: { anchorKind: 'position', position: [...target] } };
            }
            const existing = stepActions.findIndex((entry) => entry.keyframe === action.keyframe);
            if (existing >= 0) stepActions.splice(existing, 1);
            stepActions.push({
                ...action, view: currentView,
                ...(action.stay === undefined ? {} : { stay: Boolean(stay) }),
                ...(direction.navigation === 'before-frame' ? { sequential: true } : {}),
                ...(direction.hold === 'none' ? {} : { holdBefore: direction.hold === 'always' || settledSinceDispatch }),
            });
            if (cue !== 'counter-copy') lastTarget = action.dispatch && action.inPlace ? undefined : [...target];
        };
        for (const [keyframeIndex, keyframe] of step.keyframes.entries()) {
            if (step.moment.step.kind === 'remaining-repetitions-copy') {
                const helper = keyframe.wheels.find(wheel => wheel.role === 'remaining-repetitions');
                const view = graph.views[args.viewByStepIndex[stepIndex]!];
                if (!helper || !view) throw new Error('Counter copy requires a wheel and working view.');
                const aim = deriveFlightAnchor({
                    spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing,
                    wheels: keyframe.wheels, movingEntity: { wheelRole: 'remaining-repetitions' },
                    stable: helper.variant === undefined,
                });
                if (!aim) throw new Error('Counter copy requires a positioned anchor.');
                if (helper.variant === 'flight') {
                    record('counter-copy', { keyframe: keyframeIndex, view: '', dispatch: keyframeIndex === 0,
                        ...(keyframeIndex === 0 ? { dispatchSource: { wheelRole: 'multiplier' as const } } : {}),
                        afterSettlement: false },
                        view.station, aim);
                }
            }
            const emission = keyframe.events.find(({ event }) =>
                event.eventKind === 'contribution-traveller-taken-off' || event.eventKind === 'operand-wheel-transfer-started')?.event;
            if (emission && firstDispatch) {
                firstDispatch = false;
                const movingEntity = toTakeoffMovingEntity(emission);
                if (movingEntity?.wheelRole === 'multiplicand') {
                    for (let index = stepIndex; index < args.steps.length && !openingMultiplicationAim; index++) {
                        const at = args.steps[index]!.keyframes.findIndex((frame) =>
                            frame.events.some(({ event }) => event.eventKind === 'unit-introduced'));
                        if (at >= 0) {
                            const decrement = deriveDecrementAim(index, at)!;
                            const source = deriveFlightAnchor({
                                spaceKey: args.spaceKey, config: args.config, scope: args.scope,
                                placing: args.placing, wheels: keyframe.wheels, movingEntity, stable: true,
                            });
                            if (!source) throw new Error('Opening multiplication requires a Multiplicand anchor.');
                            openingMultiplicationAim = decrement.takeoff ? decrement.aim
                                : [0, 1, 2].map((axis) => (decrement.aim[axis]! + source[axis]!) / 2) as Vector3Tuple;
                        }
                    }
                }
            }
            const settlement = emission === undefined && keyframe.events.some(({ event }) =>
                event.eventKind === 'contribution-traveller-settled-on-result' || event.eventKind === 'operand-wheel-transfer-settled-on-result');
            const alignmentEvent = keyframe.events.find(({ event }) => event.eventKind === 'contribution-traveller-aligned')?.event;
            const alignment = alignmentEvent !== undefined;
            const settles = keyframe.events.some(({ event }) =>
                event.eventKind === 'contribution-traveller-settled-on-result' || event.eventKind === 'operand-wheel-transfer-settled-on-result');
            const afterSettlement = emission !== undefined && settledSinceDispatch;
            // After a Multiplier decrement or a Divisor revival the next operand is in view, so its dispatch is cued in place.
            const inPlace = emission !== undefined && decrementedSinceDispatch;
            if (emission) {
                settledSinceDispatch = false;
                decrementedSinceDispatch = false;
                awaitingRevival = undefined;
            }
            if (settles) settledSinceDispatch = true;
            if (keyframe.events.some(({ event }) => event.eventKind === 'unit-decrement-completed')) {
                decrementedSinceDispatch = true;
                openingMultiplicationAim = undefined;
            }
            if (emission) {
                alignedContributionTraveller = undefined;
                standoff = 'idle';
            }
            if (alignmentEvent?.eventKind === 'contribution-traveller-aligned') {
                alignedContributionTraveller = alignmentEvent.contributionTraveller;
            }
            if (emission && 'contributionTraveller' in emission
                && isSelfIncrement(args.steps, stepIndex, keyframeIndex, emission.contributionTraveller)) {
                selfIncrement = true;
                movingEntity = undefined;
            }
            if (selfIncrement) {
                if (settles) {
                    selfIncrement = false;
                    awaitingRevival = findNextMovingEntity(args.steps, stepIndex, keyframeIndex);
                }
                continue;
            }
            // The next operand (the division Divisor) revives after a self-increment: pan to it first so the
            // revival is seen, after holding the settled increment; its dispatch then follows in place.
            if (awaitingRevival && stationId) {
                const previousWheels = keyframeIndex > 0 ? step.keyframes[keyframeIndex - 1]!.wheels
                    : args.steps[stepIndex - 1]?.keyframes.at(-1)?.wheels ?? [];
                const role = awaitingRevival.wheelRole;
                const power = 'power' in awaitingRevival ? awaitingRevival.power : undefined;
                const opacity = (wheels: readonly PrimitiveWheelState[]) => {
                    const wheel = wheels.find((entry) => entry.role === role && entry.variant === undefined);
                    if (!wheel) return 0;
                    const powers = power === undefined ? wheel.powers : [power];
                    return Math.min(...powers.map((entry) => wheel.bandOpacityByPower?.[entry] ?? 1));
                };
                if (opacity(keyframe.wheels) > opacity(previousWheels)) {
                    const aim = deriveFlightAnchor({
                        spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing, wheels: keyframe.wheels,
                        movingEntity: awaitingRevival, stable: true,
                    });
                    if (!aim) throw new Error(`Natural revival has no anchor in ${step.stepKey}.`);
                    record('revival', { keyframe: keyframeIndex, view: '', dispatch: false, afterSettlement: false }, stationId, aim);
                    decrementedSinceDispatch = true;
                    decrementView = { station: stationId, aim: [...aim] };
                    awaitingRevival = undefined;
                    continue;
                }
            }
            // A unit decrement (multiplication) is watched by panning from the current station onto its receiver band.
            const decrement = deriveDecrementAim(stepIndex, keyframeIndex);
            if (decrement && stationId) {
                const { takeoff } = decrement;
                const aim = openingMultiplicationAim ?? decrement.aim;
                // Already aimed there (by the preceding settlement pan): only hold, no second pan.
                const stay = lastTarget !== undefined && aim.every((value, axis) => value === lastTarget![axis]);
                record('decrement', { keyframe: keyframeIndex, view: '', dispatch: false, afterSettlement: false,
                    ...(stay ? { stay } : {}) }, stationId, aim);
                decrementView = takeoff ? { station: stationId, aim: [...aim] } : undefined;
            }
            const standoffAssembly = keyframe.events.some(({ event }) => event.eventKind === 'assembly-introduced'
                && (event.assemblyRole === 'carry' || event.assemblyRole === 'deficit') && event.location.locationKind === 'standoff-far');
            if (standoffAssembly && standoff === 'idle' && stationId?.includes(':working:') && alignedContributionTraveller) {
                standoffTarget = deriveFlightAnchor({
                    spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing, wheels: keyframe.wheels, movingEntity: alignedContributionTraveller,
                });
                if (standoffTarget) standoff = 'pending';
            }
            // The assembly is introduced invisibly with the zero crossing (marked by overhang colouring at the Result-settlement station);
            // the camera leaves for the standoff station so the assembly's fade-in is first seen from there.
            const assemblyVisible = keyframe.wheels.some((wheel) => wheel.variant !== 'flight'
                && wheel.receiverLocation?.locationKind === 'standoff-far'
                && Object.values(wheel.bandOpacityByPower ?? {}).some((opacity) => (opacity ?? 0) > 0));
            if (!emission && stationId && receiverTarget && standoff === 'pending' && standoffTarget && assemblyVisible) {
                record('standoff-entry', { keyframe: keyframeIndex, view: '', dispatch: false, afterSettlement: false },
                    toPrimitiveStandoffStationId(stationId), standoffTarget);
                standoff = 'active';
            } else if (!emission && stationId && receiverTarget && standoff === 'returning') {
                record('standoff-return', { keyframe: keyframeIndex, view: '', dispatch: false, afterSettlement: false },
                    stationId, receiverTarget);
                standoff = 'idle';
            }
            // The watch ends once the assembly has joined the movingEntity: composed (carry) or settled on it (deficit).
            if (standoff === 'active' && keyframe.events.some(({ event }) => event.eventKind === 'composite-formed'
                || event.eventKind === 'assembly-settled-on-operand')) {
                standoff = 'returning';
            }
            if (emission) {
                movingEntity = toTakeoffMovingEntity(emission);
                const receiverView = graph.views[args.viewByStepIndex[stepIndex]!];
                if (!receiverView || receiverView.target.anchorKind !== 'position') {
                    throw new Error('Natural dispatch requires a receiver camera target.');
                }
                stationId = receiverView.station;
                receiverTarget = receiverView.target.position;
                lastTarget = undefined;
                returnToReceiver = false;
            }
            if ((!movingEntity && !settlement && !returnToReceiver) || !stationId || !receiverTarget) continue;
            let target = receiverTarget;
            if (settlement && lastTarget && receiverTarget.some((value, axis) => value !== lastTarget![axis])) {
                // The settlement pans anyway and a decrement follows in this step: pan straight to the decrement aim
                // instead of overshooting and returning. A settlement already in view stays watched in place.
                const next = step.keyframes.findIndex((entry, at) => at > keyframeIndex
                    && entry.events.some(({ event }) => event.eventKind === 'unit-introduced'));
                if (next >= 0) target = deriveDecrementAim(stepIndex, next)!.aim;
            }
            if (!settlement && movingEntity) {
                if (!keyframe.wheels.some((wheel) => wheel.role === movingEntity!.wheelRole && wheel.variant === 'flight')) {
                    throw new Error(`Natural moving entity has no flight geometry in ${step.stepKey}.`);
                }
                const anchor = deriveFlightAnchor({
                    spaceKey: args.spaceKey, config: args.config, scope: args.scope, placing: args.placing, wheels: keyframe.wheels, movingEntity,
                });
                if (!anchor) throw new Error(`Natural movingEntity has no positioned anchor in ${step.stepKey}.`);
                target = anchor;
            }
            // The empty Product's first contribution and first decrement share one operand-facing view.
            target = openingMultiplicationAim ?? target;
            const dispatch = emission !== undefined;
            const claimed = stepActions.some((entry) => entry.keyframe === keyframeIndex);
            if (!claimed) {
                // An in-place dispatch records the view the camera actually holds, so stop/resume keeps that framing.
                const held = dispatch && inPlace ? decrementView : undefined;
                const cue = dispatch ? 'dispatch' : settlement ? 'settlement' : movingEntity ? 'flight' : 'receiver-return';
                record(cue, { keyframe: keyframeIndex, view: '', dispatch, afterSettlement, ...(dispatch && inPlace ? { inPlace } : {}) },
                    held?.station ?? stationId, held?.aim ?? target);
            }
            if (emission) decrementView = undefined;
            if (settlement || alignment) movingEntity = undefined;
            returnToReceiver = alignment && openingMultiplicationAim === undefined;
        }
        actions.push(stepActions);
        endViews.push(currentView);
    }
    return { graph, actions, endViews };
}

export function withNaturalCameraChoreography(args: {
    base: SpaceDerivationFrame;
    step: PrimitiveStepState;
    phases: readonly TransitionPhase[];
    actions: readonly NaturalCameraAction[];
    camera: PrimitiveCameraConfig;
    config: Config;
    previousWheels: readonly PrimitiveWheelState[];
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    /** Phase keys are `${prefix}:${keyframe}`; defaults to the step key (primitive-space). */
    transitionKeyPrefix?: string;
    /** The rendered rod scope, for workflow operation rods. */
    scope?: string;
}): TransitionPhase[] {
    if (args.camera.base.navigationMode !== 'guided') return [...args.phases];
    const graph = args.base.spaceRenderOptions?.camera?.graph;
    if (!graph) throw new Error('Natural camera choreography requires a camera graph.');
    const prefix = args.transitionKeyPrefix ?? args.step.stepKey;
    return args.phases.flatMap((phase) => {
        const action = args.actions.find((entry) => phase.transitionOptions.transitionKey === `${prefix}:${entry.keyframe}`);
        if (!action) return [phase];
        const frame = withPrimitiveCameraDestination(args.base, graph, action.view, args.camera);
        const navigation = deriveCameraGraphNavigationPhase(frame, undefined);
        if (!navigation) throw new Error('Natural camera choreography requires a guided destination.');
        if (action.dispatch) {
            if (!args.step.moment) throw new Error(`Dispatch direction requires a moment for '${args.step.stepKey}'.`);
            const contour = selectPrimitiveDirectionEffects({ moment: args.step.moment, role: 'dispatch' }, args.directionCatalog)
                .some(effect => effect.visual === 'dispatch-contour');
            const keyframe = args.step.keyframes[action.keyframe]!;
            const emission = keyframe.events.find(({ event }) =>
                event.eventKind === 'contribution-traveller-taken-off' || event.eventKind === 'operand-wheel-transfer-started')?.event;
            const movingEntity = action.dispatchSource ?? (emission && toTakeoffMovingEntity(emission));
            if (!movingEntity) throw new Error('Dispatch attention cue requires a recognized takeoff event.');
            const cue = deriveDispatchCue({
                base: args.base, config: args.config,
                wheels: action.keyframe === 0 ? args.previousWheels : args.step.keyframes[action.keyframe - 1]!.wheels,
                movingEntity, contour,
                ...(args.scope === undefined ? {} : { scope: args.scope }),
            });
            const key = phase.transitionOptions.transitionKey;
            const settlementHold: TransitionPhase[] = action.afterSettlement && !action.inPlace ? [{
                patch: {},
                transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:settlement-hold`,
                    delay: args.camera.natural.dispatchHoldDurationMs },
            }] : [];
            return [...settlementHold, ...(action.inPlace ? [] : [navigation]), {
                patch: cue.highlight,
                transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:dispatch-highlight` },
            }, {
                patch: {},
                transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:dispatch-hold`,
                    delay: args.camera.natural.dispatchHoldDurationMs },
            }, {
                patch: cue.restore,
                transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:dispatch-clear` },
            }, phase];
        }
        if (action.sequential) {
            const hold: TransitionPhase[] = action.holdBefore ? [{
                patch: {},
                transitionOptions: { transitionKind: 'instant', transitionKey: `${phase.transitionOptions.transitionKey}:standoff-hold`,
                    delay: args.camera.natural.dispatchHoldDurationMs },
            }] : [];
            return [...hold, ...(action.stay ? [] : [navigation]), phase];
        }
        if (phase.transitionOptions.transitionKind === 'interpolated') {
            return [{ ...phase, cameraNavigation: { view: action.view } }];
        }
        return [navigation, phase];
    });
}
